import { Link } from "react-router-dom";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useWebsiteBlogAll, useWebsiteDeleteBlogPost } from "@/queries/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@satyrsmc/shared/components";
import { BookOpen, Plus, Pencil, Trash2 } from "lucide-react";
import { BlogPostStatus } from "@satyrsmc/shared/client";
import { formatTimestamp } from "@/lib/date-utils";

export function WebsiteBlogPanel() {
  const { confirm, confirmDialog } = useConfirm();
  const { data: posts = [], isLoading } = useWebsiteBlogAll();
  const deleteMutation = useWebsiteDeleteBlogPost();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="size-5" />
            Blog
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Publish and manage blog posts on the public site. Create posts with rich content and
            per-post SEO, and control draft/published status.
          </p>
          <Button asChild className="gap-2">
            <Link to="/admin/website/blog/new">
              <Plus className="size-4" />
              Add post
            </Link>
          </Button>
          {isLoading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : posts.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No blog posts yet.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {posts.map((post) => (
                <li
                  key={post.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">
                      {formatTimestamp(post.published_at ?? post.created_at)}
                    </span>
                    <span className="font-medium">{post.title}</span>
                    {post.status === BlogPostStatus.Draft && (
                      <Badge variant="secondary">Draft</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" aria-label="Edit" asChild>
                      <Link to={`/admin/website/blog/${post.id}`}>
                        <Pencil className="size-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete"
                      onClick={async () => {
                        if (await confirm(`Delete "${post.title}"? This cannot be undone.`)) {
                          deleteMutation.mutate(post.id);
                        }
                      }}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      {confirmDialog}
    </div>
  );
}
